import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Divider,
    Dropdown,
    Option,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { Settings24Regular, ArrowReset24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { setAgentOverride, clearAgentOverrides, setAiProviderSelection } from '../../redux/features/app/appSlice';
import {
    AgentConfigResponse,
    AgentOverride,
    ModelInfo,
    ProviderInfo,
    WorkflowAgentInfo,
} from '../../libs/models/AgentConfig';
import { AgentConfigService } from '../../libs/services/AgentConfigService';
import { reconcileProviderSelection } from '../../libs/utils/providerSelection';
import { applyAgentOverrideChange } from './agentOverrideChange';

// A provider is pickable only if the server reports it configured AND it exposes at least one model.
// An available-but-empty roster has no resolvable default model, so selecting it would emit a
// provider-only override ({ provider: 'x' }, model undefined) that 400s server-side as not-in-roster
// (#2388) — the same failure the empty gate below prevents for not-configured providers.
const isProviderSelectable = (provider: ProviderInfo): boolean => provider.isAvailable && provider.models.length > 0;

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: '900px',
        width: '90vw',
    },
    tableContainer: {
        maxHeight: '50vh',
        overflowY: 'auto',
    },
    table: {
        width: '100%',
    },
    categoryHeader: {
        backgroundColor: tokens.colorNeutralBackground3,
        fontWeight: 'bold',
    },
    dropdown: {
        minWidth: '150px',
    },
    overrideIndicator: {
        color: tokens.colorPaletteGreenForeground1,
        marginLeft: '4px',
    },
    headerActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    triggerButton: {
        minWidth: 'auto',
    },
    statusText: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
    },
    defaultSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
    },
    defaultRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    defaultField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '180px',
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: '8px',
    },
    divider: {
        marginTop: '8px',
        marginBottom: '16px',
    },
});

interface AgentConfigPanelProps {
    isVisible: boolean;
}

export const AgentConfigPanel: React.FC<AgentConfigPanelProps> = ({ isVisible }) => {
    const styles = useStyles();
    const dispatch = useAppDispatch();
    const accessToken = useAppSelector((state) => state.app.authToken);
    const agentOverrides = useAppSelector((state) => state.app.agentOverrides);
    const aiProviderSelection = useAppSelector((state) => state.app.aiProviderSelection);

    const [config, setConfig] = useState<AgentConfigResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);

    const agentConfigService = useMemo(() => new AgentConfigService(accessToken ?? ''), [accessToken]);

    // Use ref to always have current agentOverrides (avoids stale closure in callbacks)
    const agentOverridesRef = useRef(agentOverrides);
    agentOverridesRef.current = agentOverrides;

    // Read the selection through a ref so loadConfig can reconcile against it WITHOUT taking it as a
    // dependency — depending on it would re-create loadConfig on every reconcile dispatch and re-fire the
    // effects below in a loop.
    const aiProviderSelectionRef = useRef(aiProviderSelection);
    aiProviderSelectionRef.current = aiProviderSelection;

    // Returns true only when the config was fetched and reconciled successfully, so the one-shot mount
    // reconcile below can flip its guard only on success (a transient fetch failure must not burn the
    // single attempt). (#2440)
    const loadConfig = useCallback(async (): Promise<boolean> => {
        if (!accessToken) {
            return false;
        }

        setIsLoading(true);
        setError('');
        try {
            const response = await agentConfigService.getAgentConfigAsync();
            setConfig(response);

            // Reconcile the persisted global selection against the freshly fetched roster: a provider or
            // pinned model removed since it was stored would otherwise 400 every chat turn (#2436) until
            // cleared. Only dispatch when a field actually changed so an already-valid selection can't loop
            // the effects (reconcile returns the same reference in that case). (#2440)
            const current = aiProviderSelectionRef.current;
            const reconciled = reconcileProviderSelection(current, response.providers);
            if (reconciled.providerId !== current.providerId || reconciled.modelId !== current.modelId) {
                dispatch(setAiProviderSelection(reconciled));
            }
            return true;
        } catch (err) {
            setError('Failed to load agent configuration');
            console.error('Error loading agent config:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [accessToken, agentConfigService, dispatch]);

    // Load configuration when the dialog opens.
    useEffect(() => {
        if (isOpen) {
            void loadConfig();
        }
    }, [isOpen, loadConfig]);

    // Reconcile a persisted global selection once on mount, even if the panel is never opened — otherwise a
    // provider/model that has since been removed bricks every chat turn for an internal user who never
    // thinks to open settings. Fires for ANY persisted selection, including a lone modelId (which reconcile
    // clears as unrecoverable), not just a providerId. Guarded so it runs a single time once auth is
    // available — and the guard flips only after a SUCCESSFUL reconcile so a transient fetch failure doesn't
    // burn the one attempt. (#2440)
    const didReconcileSelectionRef = useRef(false);
    useEffect(() => {
        const selection = aiProviderSelectionRef.current;
        if (!didReconcileSelectionRef.current && accessToken && (selection.providerId || selection.modelId)) {
            void loadConfig().then((ok) => {
                if (ok) {
                    didReconcileSelectionRef.current = true;
                }
            });
        }
    }, [accessToken, loadConfig]);

    // Resolve a provider's default model id: the roster's IsDefault model, else the first listed. Mirrors
    // AiProviderPicker so both pickers agree; picking the first reasoning-capable model instead would
    // silently override the configured default (e.g. OpenAI's gpt-4o) with a costlier reasoning model
    // (#2388). Shared by the per-agent and global provider pickers.
    const getDefaultModelForProvider = useCallback(
        (providerId: string | undefined): string | undefined => {
            if (!providerId) {
                return undefined;
            }
            const provider = config?.providers.find((p) => p.id === providerId);
            return (provider?.models.find((m) => m.isDefault) ?? provider?.models[0])?.id;
        },
        [config],
    );

    // The three column handlers share one merge rule (see applyAgentOverrideChange): the reset <Option>
    // sends value="", which must clear only its own column and never the whole row (#2425).
    const handleProviderChange = useCallback(
        (agentName: string, providerId: string | undefined) => {
            const currentOverride = agentOverridesRef.current[agentName] ?? {};
            const newOverride = applyAgentOverrideChange(
                currentOverride,
                'provider',
                providerId,
                getDefaultModelForProvider,
            );
            dispatch(setAgentOverride({ agentName, override: newOverride }));
        },
        [dispatch, getDefaultModelForProvider],
    );

    const handleModelChange = useCallback(
        (agentName: string, modelId: string | undefined) => {
            const currentOverride = agentOverridesRef.current[agentName] ?? {};
            const newOverride = applyAgentOverrideChange(currentOverride, 'model', modelId, getDefaultModelForProvider);
            dispatch(setAgentOverride({ agentName, override: newOverride }));
        },
        [dispatch, getDefaultModelForProvider],
    );

    const handleReasoningChange = useCallback(
        (agentName: string, reasoning: string | undefined) => {
            const currentOverride = agentOverridesRef.current[agentName] ?? {};
            const newOverride = applyAgentOverrideChange(
                currentOverride,
                'reasoningEffort',
                reasoning,
                getDefaultModelForProvider,
            );
            dispatch(setAgentOverride({ agentName, override: newOverride }));
        },
        [dispatch, getDefaultModelForProvider],
    );

    const handleResetAll = useCallback(() => {
        dispatch(clearAgentOverrides());
        dispatch(setAiProviderSelection({}));
    }, [dispatch]);

    const handleDefaultProviderChange = useCallback(
        (providerId: string | undefined) => {
            if (!providerId) {
                dispatch(setAiProviderSelection({}));
                return;
            }
            dispatch(
                setAiProviderSelection({
                    providerId,
                    modelId: getDefaultModelForProvider(providerId),
                }),
            );
        },
        [dispatch, getDefaultModelForProvider],
    );

    const handleDefaultModelChange = useCallback(
        (modelId: string | undefined) => {
            dispatch(
                setAiProviderSelection({
                    ...aiProviderSelection,
                    modelId: modelId ?? undefined,
                }),
            );
        },
        [dispatch, aiProviderSelection],
    );

    const getModelsForProvider = useCallback(
        (providerId: string | undefined): ModelInfo[] => {
            if (!providerId || !config) {
                return [];
            }
            const provider = config.providers.find((p) => p.id === providerId);
            return provider?.models ?? [];
        },
        [config],
    );

    const getEffectiveProvider = useCallback(
        (agent: WorkflowAgentInfo): string => {
            const override = agentOverrides[agent.name] as AgentOverride | undefined;
            return override?.provider ?? agent.currentProvider;
        },
        [agentOverrides],
    );

    const agentOverrideCount = Object.keys(agentOverrides).length;
    const hasDefaultOverride = Boolean(aiProviderSelection.providerId ?? aiProviderSelection.modelId);
    const totalOverrideCount = agentOverrideCount + (hasDefaultOverride ? 1 : 0);

    const selectedDefaultProvider = config?.providers.find((p) => p.id === aiProviderSelection.providerId);
    const defaultProviderModels = selectedDefaultProvider?.models ?? [];

    // Group agents by category
    const agentsByCategory = useMemo(() => {
        if (!config) {
            return {};
        }
        const result: Record<string, WorkflowAgentInfo[]> = {};
        for (const agent of config.agents) {
            if (!(agent.category in result)) {
                result[agent.category] = [];
            }
            result[agent.category].push(agent);
        }
        return result;
    }, [config]);

    if (!isVisible) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(_, data) => {
                setIsOpen(data.open);
            }}
        >
            <DialogTrigger disableButtonEnhancement>
                <Button
                    icon={<Settings24Regular />}
                    appearance="subtle"
                    className={styles.triggerButton}
                    title="AI Settings"
                >
                    {totalOverrideCount > 0 && <span className={styles.overrideIndicator}>({totalOverrideCount})</span>}
                </Button>
            </DialogTrigger>
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody>
                    <DialogTitle>
                        <div className={styles.headerActions}>
                            <span>AI Settings</span>
                            {totalOverrideCount > 0 && (
                                <Button
                                    icon={<ArrowReset24Regular />}
                                    appearance="subtle"
                                    onClick={handleResetAll}
                                    title="Reset all overrides"
                                >
                                    Reset All
                                </Button>
                            )}
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Text className={styles.statusText}>
                            Configure AI providers and models. Changes are applied immediately and persist across page
                            refreshes.
                        </Text>

                        {error && (
                            <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: 8 }}>
                                {error}
                            </Text>
                        )}

                        {isLoading ? (
                            <Text>Loading configuration...</Text>
                        ) : (
                            config && (
                                <>
                                    {/* Default Provider/Model Section */}
                                    <div className={styles.defaultSection}>
                                        <Text className={styles.sectionTitle}>
                                            Default Provider & Model
                                            {hasDefaultOverride && <span className={styles.overrideIndicator}>*</span>}
                                        </Text>
                                        <div className={styles.defaultRow}>
                                            <div className={styles.defaultField}>
                                                <Text size={200}>Provider</Text>
                                                <Dropdown
                                                    className={styles.dropdown}
                                                    placeholder="System default"
                                                    selectedOptions={
                                                        aiProviderSelection.providerId
                                                            ? [aiProviderSelection.providerId]
                                                            : []
                                                    }
                                                    onOptionSelect={(_, data) => {
                                                        handleDefaultProviderChange(data.optionValue);
                                                    }}
                                                >
                                                    <Option text="System default" value="">
                                                        System default
                                                    </Option>
                                                    {config.providers.map((provider) => (
                                                        // Grey out a provider that can't produce a valid
                                                        // request: one the server reports as not configured
                                                        // (e.g. Claude on a deploy whose AWS Bedrock creds
                                                        // are still unset) or one with an empty roster, so it
                                                        // can't be picked and then 400/500 on send (#2388).
                                                        <Option
                                                            key={provider.id}
                                                            text={provider.displayName}
                                                            value={provider.id}
                                                            disabled={!isProviderSelectable(provider)}
                                                        >
                                                            {provider.displayName}
                                                        </Option>
                                                    ))}
                                                </Dropdown>
                                            </div>
                                            {aiProviderSelection.providerId && (
                                                <div className={styles.defaultField}>
                                                    <Text size={200}>Model</Text>
                                                    <Dropdown
                                                        className={styles.dropdown}
                                                        placeholder="Default model"
                                                        selectedOptions={
                                                            aiProviderSelection.modelId
                                                                ? [aiProviderSelection.modelId]
                                                                : []
                                                        }
                                                        onOptionSelect={(_, data) => {
                                                            handleDefaultModelChange(data.optionValue);
                                                        }}
                                                    >
                                                        <Option text="Default model" value="">
                                                            Default model
                                                        </Option>
                                                        {defaultProviderModels.map((model) => {
                                                            const displayText = model.supportsReasoning
                                                                ? `${model.displayName} (reasoning)`
                                                                : model.displayName;
                                                            return (
                                                                <Option
                                                                    key={model.id}
                                                                    text={displayText}
                                                                    value={model.id}
                                                                >
                                                                    {displayText}
                                                                </Option>
                                                            );
                                                        })}
                                                    </Dropdown>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Divider className={styles.divider} />

                                    {/* Per-Agent Configuration Section */}
                                    <Text className={styles.sectionTitle}>
                                        Per-Agent Overrides
                                        {agentOverrideCount > 0 && (
                                            <span className={styles.overrideIndicator}>({agentOverrideCount})</span>
                                        )}
                                    </Text>
                                    <Text className={styles.statusText} style={{ marginBottom: '8px' }}>
                                        Override settings for individual workflow agents. Leave empty to use default.
                                    </Text>
                                    <div className={styles.tableContainer}>
                                        <Table className={styles.table} size="small">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHeaderCell>Agent</TableHeaderCell>
                                                    <TableHeaderCell>Provider</TableHeaderCell>
                                                    <TableHeaderCell>Model</TableHeaderCell>
                                                    <TableHeaderCell>Reasoning</TableHeaderCell>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {Object.entries(agentsByCategory).map(([category, agents]) => (
                                                    <React.Fragment key={category}>
                                                        <TableRow className={styles.categoryHeader}>
                                                            <TableCell colSpan={4}>
                                                                <TableCellLayout>{category}</TableCellLayout>
                                                            </TableCell>
                                                        </TableRow>
                                                        {agents.map((agent) => (
                                                            <AgentRow
                                                                key={agent.name}
                                                                agent={agent}
                                                                providers={config.providers}
                                                                reasoningLevels={config.reasoningEffortLevels}
                                                                override={agentOverrides[agent.name]}
                                                                effectiveProvider={getEffectiveProvider(agent)}
                                                                getModelsForProvider={getModelsForProvider}
                                                                onProviderChange={handleProviderChange}
                                                                onModelChange={handleModelChange}
                                                                onReasoningChange={handleReasoningChange}
                                                                styles={styles}
                                                            />
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </>
                            )
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="primary">Close</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

interface AgentRowProps {
    agent: WorkflowAgentInfo;
    providers: ProviderInfo[];
    reasoningLevels: string[];
    override?: AgentOverride;
    effectiveProvider: string;
    getModelsForProvider: (providerId: string | undefined) => ModelInfo[];
    onProviderChange: (agentName: string, providerId: string | undefined) => void;
    onModelChange: (agentName: string, modelId: string | undefined) => void;
    onReasoningChange: (agentName: string, reasoning: string | undefined) => void;
    styles: ReturnType<typeof useStyles>;
}

const AgentRow: React.FC<AgentRowProps> = ({
    agent,
    providers,
    reasoningLevels,
    override,
    effectiveProvider,
    getModelsForProvider,
    onProviderChange,
    onModelChange,
    onReasoningChange,
    styles,
}) => {
    const hasOverride = Boolean(override?.provider ?? override?.model ?? override?.reasoningEffort);
    const models = getModelsForProvider(effectiveProvider);

    // Find the display text for selected values
    const selectedProviderObj = override?.provider ? providers.find((p) => p.id === override.provider) : undefined;
    const selectedModelObj = override?.model ? models.find((m) => m.id === override.model) : undefined;

    // Compute display values for the dropdown buttons
    // Always provide a string value (empty string for unset) to keep Dropdown controlled
    const providerDisplayValue = selectedProviderObj?.displayName ?? override?.provider ?? '';
    const modelDisplayValue = selectedModelObj
        ? selectedModelObj.supportsReasoning
            ? `${selectedModelObj.displayName} (reasoning)`
            : selectedModelObj.displayName
        : (override?.model ?? '');
    const reasoningDisplayValue = override?.reasoningEffort ?? '';

    return (
        <TableRow>
            <TableCell>
                <TableCellLayout>
                    {agent.displayName}
                    {hasOverride && <span className={styles.overrideIndicator}>*</span>}
                </TableCellLayout>
            </TableCell>
            <TableCell>
                <Dropdown
                    className={styles.dropdown}
                    placeholder={agent.currentProvider}
                    value={providerDisplayValue}
                    selectedOptions={override?.provider ? [override.provider] : []}
                    onOptionSelect={(_, data) => {
                        onProviderChange(agent.name, data.optionValue);
                    }}
                >
                    <Option text={`Default (${agent.currentProvider})`} value="">
                        Default ({agent.currentProvider})
                    </Option>
                    {providers.map((provider) => (
                        // Same selectability gate as the global picker: a not-configured or empty-roster
                        // provider is greyed out rather than selectable-then-400/500 on send (#2388).
                        <Option
                            key={provider.id}
                            text={provider.displayName}
                            value={provider.id}
                            disabled={!isProviderSelectable(provider)}
                        >
                            {provider.displayName}
                        </Option>
                    ))}
                </Dropdown>
            </TableCell>
            <TableCell>
                <Dropdown
                    className={styles.dropdown}
                    placeholder={agent.currentModel}
                    value={modelDisplayValue}
                    selectedOptions={override?.model ? [override.model] : []}
                    onOptionSelect={(_, data) => {
                        onModelChange(agent.name, data.optionValue);
                    }}
                >
                    <Option text={`Default (${agent.currentModel})`} value="">
                        Default ({agent.currentModel})
                    </Option>
                    {models.map((model) => {
                        const displayText = model.supportsReasoning
                            ? `${model.displayName} (reasoning)`
                            : model.displayName;
                        return (
                            <Option key={model.id} text={displayText} value={model.id}>
                                {displayText}
                            </Option>
                        );
                    })}
                </Dropdown>
            </TableCell>
            <TableCell>
                <Dropdown
                    className={styles.dropdown}
                    placeholder={agent.currentReasoningEffort ?? 'None'}
                    value={reasoningDisplayValue}
                    selectedOptions={override?.reasoningEffort ? [override.reasoningEffort] : []}
                    onOptionSelect={(_, data) => {
                        onReasoningChange(agent.name, data.optionValue);
                    }}
                >
                    <Option text={`Default (${agent.currentReasoningEffort ?? 'None'})`} value="">
                        Default ({agent.currentReasoningEffort ?? 'None'})
                    </Option>
                    {reasoningLevels.map((level) => (
                        <Option key={level} text={level} value={level}>
                            {level}
                        </Option>
                    ))}
                </Dropdown>
            </TableCell>
        </TableRow>
    );
};
